/**
 * Session review (M0.3): active sessions + last 7 days, status/progress/token/
 * cost/duration, live refresh, unified activity feed, direct content display and
 * the E2E explanation. Main tasks are shown by default; subtasks (daemon
 * `meta.parent_session_id`) expand on tap. Data arrives through the E2E relay in
 * live mode and from the mock daemon in demo mode.
 */
import { useEffect, useMemo, useState } from 'react';
import { nowSeconds } from '../lib/id';
import { formatCost, formatClock, formatTokens } from '../lib/format';
import {
  MAX_ACTIVITY_ITEMS,
  SESSION_FILTERS_NONE,
  SESSION_SOURCE_LABELS,
  SESSION_STATUS_FILTER_LABELS,
  SESSION_WINDOW_DAYS,
  buildActivityFeed,
  buildSessionTree,
  filterSessions,
  flattenSessionTree,
  groupSessions,
  sessionFilterCounts,
  sessionHierarchyCounts,
  sessionKey,
  sessionTotals,
  type SessionFilters,
  type SessionStatusFilter,
} from '../lib/sessions';
import { filterMonitoredSessions } from '../lib/session-monitoring';
import { useApp } from '../state/AppState';
import type { SessionSource } from '@luminis/protocol';
import type { SessionView } from '../lib/types';
import { ActivityFeed } from '../components/ActivityFeed';
import { E2ENote } from '../components/E2ENote';
import { Icon } from '../components/Icon';
import { QqSessionsCard } from '../components/QqSessionsCard';
import { SessionCard } from '../components/SessionCard';
import { SessionDetail } from '../components/SessionDetail';
import { StatusBanner } from '../components/StatusBanner';

const LIVE_REFRESH_MS = 5000;
const IDLE_TICK_MS = 30_000;

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0f1629] px-3 py-2.5">
      <p className="text-[10px] text-slate-500">{label}</p>
      <p className="mt-0.5 truncate font-mono text-sm text-slate-100">{value}</p>
    </div>
  );
}

function GroupHeading({ title, count }: { title: string; count: number }) {
  return (
    <div className="flex items-center gap-2 pt-1">
      <h2 className="text-xs font-medium text-slate-300">{title}</h2>
      <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-400">
        {count}
      </span>
      <span className="h-px flex-1 bg-white/5" />
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick(): void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] transition ${
        active
          ? 'border-indigo-400/50 bg-indigo-400/15 text-indigo-100'
          : 'border-white/10 bg-white/5 text-slate-400'
      }`}
    >
      {label} <span className={active ? 'text-indigo-200/80' : 'text-slate-600'}>{count}</span>
    </button>
  );
}

function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-8 shrink-0 text-[10px] text-slate-500">{label}</span>
      <div className="flex min-w-0 gap-1.5 overflow-x-auto pb-1">{children}</div>
    </div>
  );
}

export function SessionsPage() {
  const {
    sessions,
    refreshSessions,
    sessionError,
    settings,
    relayPlan,
    network,
    sessionExclusions,
    sessionMonitoringNotice,
    dismissSessionMonitoringNotice,
  } = useApp();
  const [tab, setTab] = useState<'sessions' | 'activity'>('sessions');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [live, setLive] = useState(true);
  /** Roots whose subtasks are expanded (collapsed by default). */
  const [expandedSubtasks, setExpandedSubtasks] = useState<ReadonlySet<string>>(new Set());
  /** Optional debug switch: render the whole tree expanded. */
  const [showAllSubtasks, setShowAllSubtasks] = useState(false);
  /** Task-28 tag chips (source/status); defaults to “all”. */
  const [sourceFilter, setSourceFilter] = useState<SessionSource | null>(null);
  const [statusFilter, setStatusFilter] = useState<SessionStatusFilter | null>(null);
  const [, setTick] = useState(0);
  const now = nowSeconds();
  const liveChannel = settings.demoMode || relayPlan.enabled;

  // Locally excluded sessions never render, even before the daemon-side
  // `session.list` filter lands (optimistic hide + offline cache).
  const visibleSessions = useMemo(
    () => filterMonitoredSessions(sessions, sessionExclusions),
    [sessions, sessionExclusions],
  );
  const groups = useMemo(
    () => groupSessions(visibleSessions, now, SESSION_WINDOW_DAYS),
    [visibleSessions, now],
  );
  const visible = useMemo(() => [...groups.active, ...groups.recent], [groups]);
  const filters = useMemo<SessionFilters>(
    () => ({ source: sourceFilter, status: statusFilter }),
    [sourceFilter, statusFilter],
  );
  const filterCounts = useMemo(() => sessionFilterCounts(visible), [visible]);
  const filtered = useMemo(() => filterSessions(visible, filters), [visible, filters]);
  const totals = useMemo(() => sessionTotals(filtered), [filtered]);
  const activityCount = useMemo(() => buildActivityFeed(visible).length, [visible]);
  const tree = useMemo(() => buildSessionTree(filtered), [filtered]);
  const hierarchy = useMemo(() => sessionHierarchyCounts(filtered, tree), [filtered, tree]);
  const activeRoots = useMemo(
    () => tree.roots.filter((node) => node.session.status === 'active'),
    [tree],
  );
  const recentRoots = useMemo(
    () => tree.roots.filter((node) => node.session.status !== 'active'),
    [tree],
  );
  const activeRows = useMemo(
    () => flattenSessionTree(activeRoots, expandedSubtasks, { expandAll: showAllSubtasks }),
    [activeRoots, expandedSubtasks, showAllSubtasks],
  );
  const recentRows = useMemo(
    () => flattenSessionTree(recentRoots, expandedSubtasks, { expandAll: showAllSubtasks }),
    [recentRoots, expandedSubtasks, showAllSubtasks],
  );
  const hiddenSubtaskCount = filtered.length - tree.roots.length;
  const filtersActive = sourceFilter !== null || statusFilter !== null;
  const clearFilters = (): void => {
    setSourceFilter(SESSION_FILTERS_NONE.source);
    setStatusFilter(SESSION_FILTERS_NONE.status);
  };
  const selected = useMemo(
    () => visibleSessions.find((session) => sessionKey(session) === selectedKey) ?? null,
    [visibleSessions, selectedKey],
  );

  // Source chip order follows the shared label map; only present sources show.
  const sourcesPresent = useMemo(
    () =>
      (Object.keys(SESSION_SOURCE_LABELS) as SessionSource[]).filter(
        (source) => (filterCounts.bySource.get(source) ?? 0) > 0,
      ),
    [filterCounts],
  );

  const toggleSubtasks = (target: SessionView): void => {
    const key = sessionKey(target);
    setExpandedSubtasks((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Live refresh: drives relative times; demo ticks the mock and live mode asks
  // the daemon for a session snapshot over the E2E relay.
  useEffect(() => {
    const period = live && liveChannel ? LIVE_REFRESH_MS : IDLE_TICK_MS;
    if (live && liveChannel) refreshSessions();
    const timer = setInterval(() => {
      setTick((value) => value + 1);
      if (live && liveChannel) refreshSessions();
    }, period);
    return () => clearInterval(timer);
  }, [live, liveChannel, refreshSessions]);

  if (selected) {
    return (
      <SessionDetail
        session={selected}
        now={now}
        live={live}
        onLiveChange={setLive}
        onBack={() => setSelectedKey(null)}
      />
    );
  }

  const bridgeNotice = !settings.demoMode && !relayPlan.enabled;

  return (
    <div className="h-full overflow-y-auto">
      <StatusBanner />
      {sessionMonitoringNotice ? (
        <div className="px-4 pb-2">
          <section
            data-testid="session-monitoring-notice"
            className={`flex items-start justify-between gap-2 rounded-xl border px-3 py-2 text-[11px] leading-relaxed ${
              sessionMonitoringNotice.tone === 'success'
                ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
                : sessionMonitoringNotice.tone === 'error'
                  ? 'border-rose-400/30 bg-rose-400/10 text-rose-200'
                  : 'border-amber-400/30 bg-amber-400/10 text-amber-200'
            }`}
          >
            <span className="min-w-0 break-words">{sessionMonitoringNotice.message}</span>
            <button
              type="button"
              onClick={dismissSessionMonitoringNotice}
              aria-label="关闭监视状态提示"
              className="shrink-0 rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-300"
            >
              关闭
            </button>
          </section>
        </div>
      ) : null}
      <div className="px-4 pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-base font-semibold text-slate-100">会话审阅</h1>
            <p className="text-[11px] text-slate-500">
              默认只看主任务 · 活动中的 + 最近 {SESSION_WINDOW_DAYS} 天 · 只读 · 内容直接展示
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => setLive((value) => !value)}
              aria-pressed={live}
              className={`flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-[11px] ${
                live
                  ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
                  : 'border-white/10 bg-white/5 text-slate-400'
              }`}
              title={live ? '暂停实时刷新' : '开启实时刷新'}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  live ? 'animate-pulse bg-emerald-400' : 'bg-slate-500'
                }`}
              />
              {live ? '实时' : '暂停'}
            </button>
            <button
              type="button"
              onClick={refreshSessions}
              disabled={bridgeNotice}
              className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1.5 text-[11px] text-slate-300 disabled:opacity-40"
            >
              <Icon name="refresh" className="h-3.5 w-3.5" />
              刷新
            </button>
          </div>
        </div>

        <p className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-slate-400">
          <span>
            进行中{' '}
            <b className="font-mono text-sm font-semibold text-slate-100">{hierarchy.active}</b>
          </span>
          <span className="text-slate-600">·</span>
          <span>
            主任务{' '}
            <b className="font-mono text-sm font-semibold text-slate-100">{hierarchy.roots}</b>
          </span>
          <span className="text-slate-600">·</span>
          <span>
            可见{' '}
            <b className="font-mono text-sm font-semibold text-slate-100">{hierarchy.visible}</b>
            {hierarchy.subtasks > 0 ? (
              <span className="text-slate-500">（含 {hierarchy.subtasks} 分任务）</span>
            ) : null}
          </span>
          {filtersActive ? (
            <span className="rounded-full border border-indigo-400/30 bg-indigo-400/10 px-1.5 py-0.5 text-[10px] text-indigo-200">
              已筛选
            </span>
          ) : null}
        </p>

        <div className="mt-2 grid grid-cols-2 gap-2">
          <Stat
            label={filtersActive ? 'token 合计（筛选后）' : 'token 合计'}
            value={formatTokens(totals.tokens)}
          />
          <Stat
            label={filtersActive ? '花费估算（筛选后）' : '花费估算'}
            value={formatCost(totals.costCny)}
          />
        </div>

        <div className="mt-3 grid grid-cols-2 gap-1 rounded-xl border border-white/10 bg-white/[0.03] p-1">
          <button
            type="button"
            onClick={() => setTab('sessions')}
            aria-pressed={tab === 'sessions'}
            className={`rounded-lg py-1.5 text-xs transition ${
              tab === 'sessions' ? 'bg-indigo-500/20 text-indigo-100' : 'text-slate-400'
            }`}
          >
            会话{' '}
            <span className="text-[10px] text-slate-500">
              {filtersActive ? `${filtered.length}/${visible.length}` : visible.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setTab('activity')}
            aria-pressed={tab === 'activity'}
            className={`rounded-lg py-1.5 text-xs transition ${
              tab === 'activity' ? 'bg-indigo-500/20 text-indigo-100' : 'text-slate-400'
            }`}
          >
            活动流 <span className="text-[10px] text-slate-500">{activityCount}</span>
          </button>
        </div>

        {hiddenSubtaskCount > 0 ? (
          <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
            <span className="min-w-0 text-slate-500">
              默认只显示主任务 · {hiddenSubtaskCount} 个分任务已收起
            </span>
            <button
              type="button"
              onClick={() => setShowAllSubtasks((value) => !value)}
              aria-pressed={showAllSubtasks}
              className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] ${
                showAllSubtasks
                  ? 'border-indigo-400/40 bg-indigo-400/15 text-indigo-100'
                  : 'border-white/10 bg-white/5 text-slate-400'
              }`}
            >
              {showAllSubtasks ? '隐藏分任务' : '显示全部分任务'}
            </button>
          </div>
        ) : null}
      </div>

      <div className="space-y-2 px-4 pb-6">
        {bridgeNotice ? (
          <section className="rounded-2xl border border-amber-400/20 bg-amber-400/5 p-3 text-[11px] leading-relaxed text-amber-200/90">
            会话审阅 live 通道不可用：{relayPlan.reason ?? 'live relay 未配置'}。daemon 的{' '}
            <code>/sessions</code> 与 <code>/sessions/…/tail</code> 只读接口已就绪；配置 live relay
            后会话列表/增量经 E2E 中继更新（当前仅演示模式有 mock 数据）。
          </section>
        ) : !settings.demoMode ? (
          <section className="rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-3 text-[11px] leading-relaxed text-emerald-200/90">
            live relay：会话快照按 <code>session.list.request/response</code> 拉取，
            <code>session.tail</code> 增量经 E2E 中继推送；7 天窗口规则不变，内容直接展示。
          </section>
        ) : null}

        {sessionError && !settings.demoMode ? (
          <section className="rounded-2xl border border-rose-400/30 bg-rose-400/10 p-3 text-[11px] leading-relaxed text-rose-200">
            <p className="font-medium">会话列表不可用</p>
            <p className="mt-0.5 break-words">{sessionError}</p>
            <button
              type="button"
              onClick={refreshSessions}
              className="mt-2 rounded-lg border border-rose-400/40 bg-rose-400/10 px-3 py-1.5 text-xs text-rose-100"
            >
              重试
            </button>
          </section>
        ) : null}

        {tab === 'sessions' ? (
          <>
            <QqSessionsCard />

            {visible.length > 0 ? (
              <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-2.5">
                <FilterRow label="来源">
                  <FilterChip
                    label="全部"
                    count={filterCounts.all}
                    active={sourceFilter === null}
                    onClick={() => setSourceFilter(null)}
                  />
                  {sourcesPresent.map((source) => (
                    <FilterChip
                      key={source}
                      label={SESSION_SOURCE_LABELS[source]}
                      count={filterCounts.bySource.get(source) ?? 0}
                      active={sourceFilter === source}
                      onClick={() => setSourceFilter(source)}
                    />
                  ))}
                </FilterRow>
                <FilterRow label="状态">
                  <FilterChip
                    label="全部"
                    count={filterCounts.all}
                    active={statusFilter === null}
                    onClick={() => setStatusFilter(null)}
                  />
                  {(['active', 'done', 'other'] as const).map((status) => (
                    <FilterChip
                      key={status}
                      label={SESSION_STATUS_FILTER_LABELS[status]}
                      count={filterCounts.byStatus[status]}
                      active={statusFilter === status}
                      onClick={() => setStatusFilter(status)}
                    />
                  ))}
                </FilterRow>
                {filtersActive ? (
                  <div className="mt-1 flex items-center justify-between gap-2 border-t border-white/5 pt-1.5 text-[11px]">
                    <span className="min-w-0 text-slate-500">
                      筛选后 {filtered.length} 个（主任务 {hierarchy.roots}
                      {hierarchy.subtasks > 0 ? `，分任务 ${hierarchy.subtasks}` : ''}）
                    </span>
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="shrink-0 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300"
                    >
                      清除筛选
                    </button>
                  </div>
                ) : null}
              </section>
            ) : null}

            {visible.length === 0 ? (
              <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-6 text-center">
                <Icon name="sessions" className="mx-auto h-8 w-8 text-slate-600" />
                <p className="mt-2 text-sm text-slate-300">没有会话数据</p>
                <p className="mt-1 text-xs text-slate-500">
                  {settings.demoMode
                    ? '演示模式下会在配对后生成 mock 会话；真实数据由 server 会话桥（DSH/Codex/OpenCode/VS Code）采集。'
                    : '等待 daemon /sessions 经中继推送后，这里会显示活动中的会话与最近 7 天记录。'}
                </p>
              </section>
            ) : filtered.length === 0 ? (
              <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-6 text-center">
                <Icon name="sessions" className="mx-auto h-8 w-8 text-slate-600" />
                <p className="mt-2 text-sm text-slate-300">没有符合筛选的会话</p>
                <p className="mt-1 text-xs text-slate-500">
                  当前来源/状态组合为空（可见会话共 {visible.length} 个）。
                </p>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-3 rounded-lg border border-indigo-400/30 bg-indigo-400/10 px-3 py-1.5 text-xs text-indigo-100"
                >
                  清除筛选
                </button>
              </section>
            ) : null}

            {activeRows.length > 0 ? (
              <>
                <GroupHeading title="进行中（主任务）" count={activeRoots.length} />
                {activeRows.map((row) => (
                  <div
                    key={row.node.key}
                    className={row.depth > 0 ? 'ml-3 border-l border-white/10 pl-2.5' : ''}
                  >
                    <SessionCard
                      session={row.session}
                      now={now}
                      onOpen={(target) => setSelectedKey(sessionKey(target))}
                      subtaskCount={row.node.descendantCount}
                      expanded={row.expanded}
                      onToggleSubtasks={toggleSubtasks}
                    />
                  </div>
                ))}
              </>
            ) : null}

            {recentRows.length > 0 ? (
              <>
                <GroupHeading
                  title={`最近 ${SESSION_WINDOW_DAYS} 天（主任务）`}
                  count={recentRoots.length}
                />
                {recentRows.map((row) => (
                  <div
                    key={row.node.key}
                    className={row.depth > 0 ? 'ml-3 border-l border-white/10 pl-2.5' : ''}
                  >
                    <SessionCard
                      session={row.session}
                      now={now}
                      onOpen={(target) => setSelectedKey(sessionKey(target))}
                      subtaskCount={row.node.descendantCount}
                      expanded={row.expanded}
                      onToggleSubtasks={toggleSubtasks}
                    />
                  </div>
                ))}
              </>
            ) : null}

            {groups.hiddenCount > 0 ? (
              <p className="px-1 text-[11px] text-slate-500">
                另有 {groups.hiddenCount} 个非活动会话早于 {SESSION_WINDOW_DAYS}{' '}
                天，按默认订阅窗口未显示。
              </p>
            ) : null}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between px-1 text-[11px] text-slate-500">
              <span>所有可见会话的事件合并流 · 最新在前</span>
              <span>
                {activityCount > MAX_ACTIVITY_ITEMS
                  ? `最近 ${MAX_ACTIVITY_ITEMS} 条`
                  : `${activityCount} 条`}
              </span>
            </div>
            <ActivityFeed
              sessions={visible}
              now={now}
              onOpen={(target) => setSelectedKey(sessionKey(target))}
            />
          </>
        )}

        <div className="pt-2 text-[11px] text-slate-500">
          {settings.demoMode
            ? `演示数据（本机 mock daemon，每 ${LIVE_REFRESH_MS / 1000}s 刷新一次）；配对后自动出现，不连接真实 PC。`
            : 'live 会话经 E2E 中继增量更新；审阅严格只读。'}
          {network.lastSyncAt ? ` 数据截至 ${formatClock(network.lastSyncAt)}。` : ''}
        </div>

        <E2ENote />
      </div>
    </div>
  );
}
